
import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import { Code, Database, Server, Cloud, Brain, CheckCircle } from 'lucide-react';
import ContactCTA from '../components/home/ContactCTA';
import { useContentImages } from '@/hooks/useContentImages';
import servicesData from '@/data/services.json';

interface Service {
  id: string
  title: string
  description: string
  icon_name: string
  features: string[]
  technologies: string[]
  image_url: string | null
  slug: string
  display_order: number
  is_active: boolean
}

const iconMap = {
  Code: Code,
  Database: Database,
  Server: Server,
  Cloud: Cloud,
  Brain: Brain,
}

const Services = () => {
  const [services, setServices] = useState<Service[]>([])
  const { images: aboutImages } = useContentImages('services');
  const heroImage = aboutImages[0];
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Load static data
    const data = (servicesData as Service[])
      .filter(s => s.is_active)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
    setServices(data)
    setLoading(false)
  }, [])

  if (loading) {
    return (
      <Layout>
          <section className="relative flex items-center justify-center overflow-hidden">
            {/* Background Image with Overlay */}
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-800/70 to-blue-900/80 z-10"></div>
              <img
                src={heroImage?.image_url}
                alt={heroImage?.alt_text || ""}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Background Pattern */}
            <div className="absolute inset-0 z-20 opacity-10">
              <div className="absolute top-20 left-20 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
              <div className="absolute bottom-20 right-20 w-80 h-80 bg-purple-500 rounded-full blur-3xl"></div>
            </div>

            <div className="container-custom relative z-30 text-center pt-24 pb-24">
              <div className="max-w-4xl mx-auto animate-on-scroll">
                <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">Our Services</h1>
                  <p className="text-xl text-slate-200">
                    Loading services...
                  </p>
              </div>
            </div>

            {/* Scroll indicator */}
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce z-30">
              <div className="w-6 h-10 border-2 border-white/60 rounded-full flex justify-center">
                <div className="w-1 h-3 bg-white/60 rounded-full mt-2 animate-pulse"></div>
              </div>
            </div>
          </section>
        
      </Layout>
    )
  }

  return (
    <Layout>
      {/* Hero Section */}

        <section className="relative flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-800/70 to-blue-900/80 z-10"></div>
        <img
          src={heroImage?.image_url}
          alt={heroImage?.alt_text || ""}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Background Pattern */}
      <div className="absolute inset-0 z-20 opacity-10">
        <div className="absolute top-20 left-20 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-purple-500 rounded-full blur-3xl"></div>
      </div>

      <div className="container-custom relative z-30 text-center pt-24 pb-24">
        <div className="max-w-4xl mx-auto animate-on-scroll">
           <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">Our Services</h1>
            <p className="text-xl text-slate-200">
              Comprehensive IT solutions tailored to meet the unique needs of your business.
            </p>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce z-30">
        <div className="w-6 h-10 border-2 border-white/60 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white/60 rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>
    </section>
      
      {/* Services Detail */}
      <section className="py-16">
        <div className="container-custom">
          {services.length === 0 ? (
            <div className="text-center text-gray-500">
              <p>No services found. Please run the data fetch script to populate content.</p>
            </div>
          ) : (
            services.map((service, index) => {
              const IconComponent = iconMap[service.icon_name as keyof typeof iconMap] || Code
              
              return (
                <div 
                  key={service.id}
                  id={service.slug}
                  className={`py-16 ${index !== services.length - 1 ? 'border-b' : ''}`}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <div className="order-2 lg:order-1 animate-on-scroll">
                      <div className="mb-4">
                        <IconComponent size={48} className="text-goeltec-blue" />
                      </div>
                      <h2 className="text-3xl font-bold mb-4">{service.title}</h2>
                      <p className="text-lg text-gray-700 mb-6">{service.description}</p>
                      
                      {service.features && service.features.length > 0 && (
                        <>
                          <h3 className="text-xl font-semibold mb-4">Key Features</h3>
                          <ul className="space-y-3 mb-8">
                            {service.features.map((feature, i) => (
                              <li key={i} className="flex items-start">
                                <CheckCircle size={20} className="text-goeltec-blue mt-1 mr-3 flex-shrink-0" />
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                      
                      {service.technologies && service.technologies.length > 0 && (
                        <>
                          <h3 className="text-xl font-semibold mb-4">Technologies We Use</h3>
                          <div className="flex flex-wrap gap-2">
                            {service.technologies.map((tech, i) => (
                              <span 
                                key={i}
                                className="bg-goeltec-lightblue text-goeltec-blue px-3 py-1 rounded-full text-sm"
                              >
                                {tech}
                              </span>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                    <div className="order-1 lg:order-2 flex justify-center animate-on-scroll">
                      <div className="relative">
                        <div className="absolute -left-5 -top-5 w-24 h-24 bg-goeltec-blue/10 rounded-full"></div>
                        <div className="absolute -right-5 -bottom-5 w-32 h-32 bg-goeltec-orange/10 rounded-full"></div>
                        <img 
                          src={service.image_url || "/placeholder.svg"} 
                          alt={service.title} 
                          className="w-full max-w-md rounded-lg shadow-lg relative z-10"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </section>
      
      <ContactCTA />
    </Layout>
  );
};

export default Services;
